// P3-015: BattlePhase coordinator — Phase 3 entry point
// Matches ARCH section 5.3 code pattern
// Produces BattleResult (no economy calculations — that's Phase 4's job)

import type { PrepareResult, ContractResult, BattleResult, BattleStats } from '@/shared/types/phase-contracts';
import { useBattleStore } from '../stores/battleStore';
import * as BattleOrchestrator from './BattleOrchestrator';

/**
 * Initialize the battle phase.
 * Converts PrepareResult + ContractResult into internal battle state.
 */
export function initBattle(prepare: PrepareResult, contract: ContractResult): void {
  BattleOrchestrator.initBattle(prepare, contract);
}

/**
 * Finalize the battle and produce BattleResult.
 * Called after battle has ended (either VICTORY or DEFEAT).
 *
 * IMPORTANT: This does NOT contain any economy calculations.
 * Economy (FLOP/AGI rewards, penalties) is Phase 4's responsibility.
 */
export function finalize(): BattleResult | null {
  const battleStore = useBattleStore.getState();

  if (battleStore.result === null) {
    // Battle not yet finished
    return null;
  }

  const stats: BattleStats = {
    turnsUsed: battleStore.currentTurn,
    turnsTotal: battleStore.maxTurns,
    totalDamageDealt: battleStore.stats.totalDamageDealt,
    maxCombo: battleStore.stats.maxCombo,
    critCount: battleStore.stats.critCount,
    overloadEvents: battleStore.stats.overloadEvents,
    skillsTriggered: battleStore.stats.skillsTriggered,
  };

  const result: BattleResult = {
    battleOutcome: battleStore.result,
    battleStats: stats,
  };

  return result;
}

/**
 * Clean up all battle state.
 */
export function cleanup(): void {
  BattleOrchestrator.resetBattle();
}
